// Settings tab — name/description/tags + DangerZone (delete).

import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { SimpleButton, Typo, Textinput } from '../../../../ui';
import ConfirmDelete from '../../../../ui/Modal/ConfirmDelete';
import { CAMPAIGN_STATUS } from '../../constants';
import { useCampaignsActions } from '../../hooks/useCampaignsActions';
import { useCampaignPermissions } from '../../hooks/useCampaignPermissions';

const Card = styled.div`
  background: ${({ theme }) => theme.colors.NEU_0};
  border: 1px solid ${({ theme }) => theme.colors.NEU_200};
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 16px;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 16px;

  & > span:first-child {
    font-size: 13px;
    color: ${({ theme }) => theme.colors.NEU_600};
  }
`;

const DangerCard = styled(Card)`
  border-color: ${({ theme }) => theme.colors.ERROR_LIGHT};
`;

const DangerTitle = styled.div`
  color: ${({ theme }) => theme.colors.ERROR_RED};
  font-weight: 700;
  margin-bottom: 10px;
`;

const SettingsTab = ({ campaign }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const permissions = useCampaignPermissions();
  const { updateCampaign, deleteCampaign } = useCampaignsActions();
  const [name, setName] = useState(campaign?.name ?? '');
  const [description, setDescription] = useState(campaign?.description ?? '');
  const [pendingDelete, setPendingDelete] = useState(false);

  const dirty = name !== campaign?.name || description !== campaign?.description;
  const canDelete = permissions.canDelete && [CAMPAIGN_STATUS.DRAFT, CAMPAIGN_STATUS.COMPLETED].includes(campaign?.status);

  const handleSave = () => updateCampaign({ id: campaign.id, name, description });

  const handleConfirmDelete = async () => {
    await deleteCampaign(campaign.id);
    setPendingDelete(false);
    navigate('/campaigns');
  };

  return (
    <>
      <Card>
        <Typo.TextTitle style={{ marginBottom: 18 }}>{t('campaigns.settings.general', 'General')}</Typo.TextTitle>
        <Field>
          <span>{t('campaigns.settings.name', 'Name')}</span>
          <Textinput value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field>
          <span>{t('campaigns.settings.description', 'Description')}</span>
          <Textinput value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <SimpleButton variant='primary' onClick={handleSave} disabled={!dirty}>
          {t('globals.save', 'Save')}
        </SimpleButton>
      </Card>

      <DangerCard>
        <DangerTitle>{t('campaigns.settings.dangerZone', 'Danger zone')}</DangerTitle>
        {canDelete ? (
          <>
            <Typo.SmallText color='textSecondary' style={{ marginBottom: 12 }}>
              {t('campaigns.settings.deleteBody', 'Soft-deletes the campaign. Metrics and aggregate data are preserved; recipient details are anonymized after the store retention window.')}
            </Typo.SmallText>
            <SimpleButton variant='ghost' startIcon='Delete' onClick={() => setPendingDelete(true)}>
              {t('campaigns.settings.deleteCampaign', 'Delete campaign')}
            </SimpleButton>
          </>
        ) : (
          <Typo.SmallText color='textSecondary'>
            {t('campaigns.settings.deleteNotAvailable', 'Pause or complete the campaign before deleting.')}
          </Typo.SmallText>
        )}
      </DangerCard>

      <ConfirmDelete
        open={pendingDelete}
        title={t('campaigns.deleteTitle', 'Delete campaign')}
        description={t('campaigns.confirmDelete', { name: campaign?.name, defaultValue: "Delete '{{name}}'? This can't be undone." })}
        onAcceptDelete={handleConfirmDelete}
        onClose={() => setPendingDelete(false)}
      />
    </>
  );
};

export default SettingsTab;
